import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, GraduationCap, MapPin, Clock, DollarSign, ArrowRight, Building2 } from 'lucide-react';
import PersonaContactModal from '../components/PersonaContactModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Loose keyword buckets so the ?category= filter from Pathways still narrows
// the list even before admins tag programs with pathways explicitly.
const CATEGORY_KEYWORDS = {
  healthcare: ['nurs', 'health', 'medical', 'dental', 'psw', 'pharma', 'care', 'clinic'],
  technology: ['tech', 'software', 'data', 'cyber', 'network', 'it ', 'comput', 'ai', 'cloud'],
  business: ['business', 'account', 'market', 'hr', 'human resource', 'project', 'manag', 'finance', 'commerce', 'mba'],
  education: ['educ', 'teach', 'child', 'ece', 'early childhood', 'ed assistant'],
  community: ['social', 'communit', 'addiction', 'support', 'counsel'],
  trades: ['electric', 'plumb', 'weld', 'auto', 'hvac', 'trade', 'construction', 'mechan'],
  hospitality: ['culin', 'hotel', 'hospitalit', 'tour', 'chef', 'food'],
  creative: ['design', 'graphic', 'video', 'media', 'ux', 'ui', 'art', 'anim', 'photo'],
};

const CATEGORY_LABELS = {
  healthcare: 'Healthcare',
  technology: 'Technology',
  business: 'Business',
  education: 'Education',
  community: 'Community Services',
  trades: 'Skilled Trades',
  hospitality: 'Hospitality',
  creative: 'Creative Industries',
};

function programMatchesCategory(program, category) {
  if (!category) return true;
  const keywords = CATEGORY_KEYWORDS[category] || [];
  const hay = `${program.program_name || ''}`.toLowerCase();
  return keywords.some((k) => hay.includes(k));
}

function formatTuition(fee) {
  if (fee == null || Number.isNaN(Number(fee))) return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Number(fee));
}

export default function ProgramsPage() {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [modal, setModal] = useState(null); // { title, desc }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/colleges`);
        const data = await res.json();
        if (!cancelled) setColleges(data.colleges || []);
      } catch (_) {
        if (!cancelled) setColleges([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return colleges
      .map((c) => {
        const programs = (c.programs || []).filter((p) => {
          if (!programMatchesCategory(p, category)) return false;
          if (!q) return true;
          const hay = `${c.name} ${p.program_name} ${c.city || ''} ${c.province || ''}`.toLowerCase();
          return hay.includes(q);
        });
        return { ...c, programs };
      })
      .filter((c) => c.programs.length > 0);
  }, [colleges, query, category]);

  const totalPrograms = filtered.reduce((sum, c) => sum + c.programs.length, 0);
  const activeLabel = CATEGORY_LABELS[category];

  const clearCategory = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    setSearchParams(next);
  };

  const setCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('category', slug); else next.delete('category');
    setSearchParams(next);
  };

  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="programs-page">
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-14">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-4">Programs</div>
        <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[1.05] tracking-tight">
          Every program we <span className="text-brand-pine italic">stand behind.</span>
        </h1>
        <p className="mt-8 text-lg text-brand-slate leading-relaxed max-w-2xl">
          Programs from our partner colleges across Canada. Duration and tuition are updated as our admin
          team keeps them current — talk to an advisor before you decide.
        </p>

        {/* Search + category chips */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1 max-w-lg">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-obsidian/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search programs, colleges, or cities…"
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-brand-border/60 rounded-full text-sm focus:outline-none focus:border-brand-pine transition-colors"
              data-testid="programs-search"
            />
          </div>
          <div className="text-xs text-brand-obsidian/60">
            {loading ? 'Loading…' : (
              <>Showing <span className="font-semibold text-brand-obsidian">{totalPrograms}</span> program{totalPrograms === 1 ? '' : 's'} across <span className="font-semibold text-brand-obsidian">{filtered.length}</span> college{filtered.length === 1 ? '' : 's'}</>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={clearCategory}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${!category ? 'bg-brand-pine text-white' : 'bg-white text-brand-obsidian/70 hover:bg-brand-mist border border-brand-border/60'}`}
            data-testid="chip-all"
          >
            All areas
          </button>
          {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
            <button
              key={slug}
              onClick={() => setCategory(slug)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${category === slug ? 'bg-brand-pine text-white' : 'bg-white text-brand-obsidian/70 hover:bg-brand-mist border border-brand-border/60'}`}
              data-testid={`chip-${slug}`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeLabel && (
          <div className="mt-4 text-xs text-brand-obsidian/60">
            Filtered by <span className="font-semibold text-brand-pine">{activeLabel}</span>
            <button onClick={clearCategory} className="ml-2 text-brand-coral hover:underline">clear</button>
          </div>
        )}
      </section>

      {/* PROGRAM LIST */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-brand-border/50 rounded-2xl p-8 animate-pulse">
                <div className="h-6 w-2/3 bg-brand-mist rounded" />
                <div className="mt-3 h-3 w-1/3 bg-brand-mist rounded" />
                <div className="mt-8 space-y-3">
                  <div className="h-4 w-full bg-brand-mist rounded" />
                  <div className="h-4 w-5/6 bg-brand-mist rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-brand-border/50 rounded-3xl p-12 text-center" data-testid="programs-empty">
            <GraduationCap size={28} className="mx-auto text-brand-pine" />
            <h3 className="font-serif-editorial text-2xl mt-5">No programs match yet.</h3>
            <p className="text-sm text-brand-slate mt-3 max-w-md mx-auto">
              Our admin team is adding partner programs continuously. Try a different search, clear the filter,
              or talk to an advisor and we&apos;ll surface matching options for you directly.
            </p>
            <button
              onClick={() => setModal({ title: 'Help me find a program', desc: 'Not seeing what you need? An advisor can shortlist programs from our full partner network.' })}
              className="mt-6 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
              data-testid="programs-empty-advisor"
            >
              Talk to an advisor <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filtered.map((college) => (
              <div
                key={college.id}
                className="bg-white border border-brand-border/50 rounded-3xl overflow-hidden"
                data-testid={`college-card-${college.id}`}
              >
                <div className="p-8 md:p-10 border-b border-brand-border/40 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-brand-pine/10 flex items-center justify-center">
                        <Building2 size={19} className="text-brand-pine" />
                      </div>
                      <div>
                        <h2 className="font-serif-editorial text-2xl leading-tight">{college.name}</h2>
                        {(college.city || college.province) && (
                          <div className="flex items-center gap-1.5 text-xs text-brand-obsidian/60 mt-1">
                            <MapPin size={12} />
                            <span>{[college.city, college.province].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-brand-obsidian/50 sm:text-right">
                    {college.programs.length} program{college.programs.length === 1 ? '' : 's'}
                  </div>
                </div>

                <div className="divide-y divide-brand-border/30">
                  {college.programs.map((p, i) => {
                    const tuition = formatTuition(p.tuition_fee);
                    return (
                      <div
                        key={`${college.id}-${i}`}
                        className="p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 hover:bg-brand-mist/60 transition-colors"
                        data-testid={`program-row-${college.id}-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-serif-editorial text-lg leading-snug">{p.program_name}</div>
                          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-brand-obsidian/60">
                            {p.duration && (
                              <span className="inline-flex items-center gap-1.5">
                                <Clock size={12} className="text-brand-pine" /> {p.duration}
                              </span>
                            )}
                            {tuition && (
                              <span className="inline-flex items-center gap-1.5">
                                <DollarSign size={12} className="text-brand-pine" /> {tuition}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setModal({
                            title: `Ask about ${p.program_name}`,
                            desc: `${college.name}${p.duration ? ` · ${p.duration}` : ''}${tuition ? ` · ${tuition}` : ''}`,
                          })}
                          className="inline-flex items-center gap-2 bg-brand-pine hover:bg-brand-pine/90 text-white text-xs font-semibold px-5 py-2.5 rounded-full transition-colors self-start md:self-auto"
                          data-testid={`program-ask-${college.id}-${i}`}
                        >
                          Talk to an advisor <ArrowRight size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA FOOTER */}
      <section className="bg-brand-pine text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-serif-editorial text-3xl md:text-5xl leading-tight">Not seeing the right fit?</h2>
          <p className="text-white/70 max-w-xl mx-auto mt-5 leading-relaxed">
            Our partner network is wider than what fits on one page. Tell us what you&apos;re looking for and
            we&apos;ll shortlist options within one business day.
          </p>
          <button
            onClick={() => setModal({ title: 'Help me shortlist programs', desc: 'Tell an advisor about your goals — we\'ll match programs from our full partner network.' })}
            className="mt-8 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-8 py-4 rounded-full transition-colors"
            data-testid="programs-footer-cta"
          >
            Talk to an advisor <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <PersonaContactModal
        open={!!modal}
        persona={modal?.title}
        personaDesc={modal?.desc}
        onClose={() => setModal(null)}
      />
    </div>
  );
}
